import { useEffect, useRef, useState } from "react";

import { columnsForWidth, type GridColumns } from "./grid";

/**
 * How many grid columns the dashboard area has room for. Measured on the
 * grid's own box (ResizeObserver) so it follows the sidebar opening and
 * closing as well as the window; without ResizeObserver (old browsers, tests)
 * it falls back to the window width.
 */
export function useGridColumns<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [columns, setColumns] = useState<GridColumns>(() => columnsForWidth(typeof window === "undefined" ? 1200 : window.innerWidth));

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = (width: number) => setColumns((prev) => {
      const next = columnsForWidth(width);
      return next === prev ? prev : next;
    });
    if (typeof ResizeObserver === "undefined") {
      const onResize = () => measure(window.innerWidth);
      onResize();
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width) measure(width);
    });
    observer.observe(el);
    measure(el.getBoundingClientRect().width || window.innerWidth);
    return () => observer.disconnect();
  }, []);

  return { ref, columns };
}
